import {
  ATTACHMENT_BUCKET,
  attachmentError,
  resolvedAttachmentMimeType,
  sanitizeAttachmentName,
  type TaskAttachment,
} from '../../domain/attachment'
import { AppError, mapSupabaseError } from '../../lib/errors'
import { supabase } from '../../lib/supabase'
import type { Database, Json } from '../../types/database'
import type {
  CreateTaskCommand,
  Task,
  TaskDashboard,
  TaskFilters,
  TaskStatus,
  TaskUpdate,
  UpdateTaskCommand,
} from '../../types/domain'

type TaskInsert = Database['public']['Tables']['tasks']['Insert']

function toTaskInsert(command: CreateTaskCommand): TaskInsert {
  return {
    id: command.id,
    title: command.title.trim(),
    description: command.description.trim() || null,
    client_id: command.clientId,
    assignee_id: command.assigneeId,
    department_id: command.departmentId,
    source: command.source,
    priority: command.priority,
    periodicity: command.periodicity,
    received_date: command.receivedDate,
    received_time: command.receivedTime,
    due_date: command.dueDate,
    due_time: command.dueTime || null,
  }
}

export async function listTasks(filters: TaskFilters): Promise<TaskDashboard[]> {
  let query = supabase
    .from('task_dashboard')
    .select('*')
    .order('due_date', { ascending: true })
    .order('due_time', { ascending: true, nullsFirst: false })

  query = filters.archived ? query.not('archived_at', 'is', null) : query.is('archived_at', null)
  if (filters.clientId) query = query.eq('client_id', filters.clientId)
  if (filters.assigneeId) query = query.eq('assignee_id', filters.assigneeId)
  if (filters.departmentId) query = query.eq('department_id', filters.departmentId)
  if (filters.priority) query = query.eq('priority', filters.priority)
  if (filters.source) query = query.eq('source', filters.source)
  if (filters.status) query = query.eq('status', filters.status)
  if (filters.todayOnly) {
    query = query.neq('status', 'completed').or('is_overdue.eq.true,is_due_today.eq.true')
  }

  const { data, error } = await query
  if (error) throw mapSupabaseError(error)
  return data as TaskDashboard[]
}

export async function findTaskById(id: string): Promise<TaskDashboard | null> {
  const { data, error } = await supabase
    .from('task_dashboard')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  if (error) throw mapSupabaseError(error)
  return data as TaskDashboard | null
}

export async function createTask(command: CreateTaskCommand): Promise<Task> {
  const response = await supabase.from('tasks').insert(toTaskInsert(command)).select('*').single()
  if (!response.error) return response.data
  if (response.error.code === '23505') {
    const existing = await findTaskById(command.id)
    if (existing) return existing
  }
  throw mapSupabaseError(response.error)
}

export async function updateTask(command: UpdateTaskCommand): Promise<Task> {
  const row = toTaskInsert(command)
  const { id: ignoredId, ...updates } = row
  void ignoredId
  const { data, error } = await supabase
    .from('tasks')
    .update(updates as Database['public']['Tables']['tasks']['Update'])
    .eq('id', command.id)
    .select('*')
    .single()
  if (error) throw mapSupabaseError(error)
  return data
}

export async function setTaskStatus(id: string, status: TaskStatus): Promise<Task> {
  const { data, error } = await supabase
    .from('tasks')
    .update({ status })
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw mapSupabaseError(error)
  return data
}

export async function setTaskArchived(id: string, archived: boolean): Promise<Task> {
  const { data, error } = await supabase
    .from('tasks')
    .update({ archived_at: archived ? new Date().toISOString() : null })
    .eq('id', id)
    .select('*')
    .single()
  if (error) throw mapSupabaseError(error)
  return data
}

export async function listTaskUpdates(taskId: string): Promise<TaskUpdate[]> {
  const { data, error } = await supabase
    .from('task_updates')
    .select('*')
    .eq('task_id', taskId)
    .order('created_at', { ascending: false })
  if (error) throw mapSupabaseError(error)
  return data.map(toTaskUpdate)
}

function parseAttachments(value: Json): TaskAttachment[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((raw) => {
    if (!raw || Array.isArray(raw) || typeof raw !== 'object') return []
    const item = raw as Record<string, Json | undefined>
    if (
      typeof item.name !== 'string'
      || typeof item.path !== 'string'
      || typeof item.size !== 'number'
      || typeof item.mimeType !== 'string'
    ) return []
    return [{ name: item.name, path: item.path, size: item.size, mimeType: item.mimeType }]
  })
}

function toTaskUpdate(row: Database['public']['Tables']['task_updates']['Row']): TaskUpdate {
  return { ...row, attachments: parseAttachments(row.attachments) }
}

async function removeUploadedAttachments(paths: string[]): Promise<void> {
  if (!paths.length) return
  await supabase.storage.from(ATTACHMENT_BUCKET).remove(paths)
}

async function uploadTaskAttachments(
  taskId: string,
  updateId: string,
  files: File[],
): Promise<TaskAttachment[]> {
  if (!files.length) return []
  const { data: sessionData } = await supabase.auth.getSession()
  const userId = sessionData.session?.user.id
  if (!userId) throw new AppError('AUTH_INVALID', 'Sua sessão expirou. Entre novamente.')

  const uploaded: TaskAttachment[] = []
  try {
    for (const [index, file] of files.entries()) {
      const validation = attachmentError(file)
      if (validation) throw new AppError('VALIDATION', validation)
      const path = [
        userId,
        taskId,
        updateId,
        `${String(index + 1).padStart(2, '0')}-${sanitizeAttachmentName(file.name)}`,
      ].join('/')
      const { error } = await supabase.storage.from(ATTACHMENT_BUCKET).upload(path, file, {
        contentType: resolvedAttachmentMimeType(file),
        upsert: true,
      })
      if (error) throw mapSupabaseError(error)
      uploaded.push({ name: file.name, path, size: file.size, mimeType: resolvedAttachmentMimeType(file) })
    }
    return uploaded
  } catch (error) {
    await removeUploadedAttachments(uploaded.map((item) => item.path))
    throw error
  }
}

export async function getAttachmentDownloadUrl(attachment: TaskAttachment): Promise<string> {
  const { data, error } = await supabase.storage
    .from(ATTACHMENT_BUCKET)
    .createSignedUrl(attachment.path, 60, { download: attachment.name })
  if (error) throw mapSupabaseError(error)
  return data.signedUrl
}

export async function addTaskUpdate(command: {
  id: string
  taskId: string
  note: string
  files: File[]
}): Promise<TaskUpdate> {
  const attachments = await uploadTaskAttachments(command.taskId, command.id, command.files)
  const response = await supabase
    .from('task_updates')
    .insert({
      id: command.id,
      task_id: command.taskId,
      note: command.note.trim(),
      attachments: attachments as unknown as Json,
    })
    .select('*')
    .single()
  if (!response.error) return toTaskUpdate(response.data)

  const existing = await supabase
    .from('task_updates')
    .select('*')
    .eq('id', command.id)
    .maybeSingle()
  if (!existing.error && existing.data) return toTaskUpdate(existing.data)

  const mappedError = mapSupabaseError(response.error)
  if (!existing.error && mappedError.code !== 'NETWORK') {
    await removeUploadedAttachments(attachments.map((item) => item.path))
  }
  throw mappedError
}
